/**
 * WetSignAttestationBoxPage — Workflow · Wet-Sign Attestation Boxes.
 *
 * Per-(docType × state × box) attestation-checkbox catalog rendered in the FSR
 * WetSignScanner modal (Q8 wet-sign flow). Wildcards: docTypeKey='*' and/or
 * stateCode='*' apply to all. Required boxes must be checked before the FSR can
 * apply the wet-sign attestation.
 *
 * Contract (verified 2026-06-16 — entity/service field names match exactly,
 * all fields persist on insert + update; composite natural key):
 *   - Read:   GET    /v3/admin/wet-sign-attestation-box?includeInactive=true
 *   - Write:  POST   /v3/admin/wet-sign-attestation-box  (AdminComplianceService upsert)
 *   - Delete: DELETE /v3/admin/wet-sign-attestation-box/{docTypeKey}/{stateCode}/{boxKey}
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface Row {
  docTypeKey: string;
  stateCode: string;
  boxKey: string;
  boxLabel: string;
  displayOrder: number;
  isRequired: boolean;
  helpText: string | null;
  isActive: boolean;
}

interface EditDraft extends Row {
  isNew: boolean;
}

function emptyDraft(): EditDraft {
  return {
    docTypeKey: '*',
    stateCode: '*',
    boxKey: '',
    boxLabel: '',
    displayOrder: 100,
    isRequired: true,
    helpText: '',
    isActive: true,
    isNew: true,
  };
}

export function WetSignAttestationBoxPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const r = await client.http.get<{ success: boolean; data?: ReadonlyArray<Row>; error?: string }>(
          '/v3/admin/wet-sign-attestation-box?includeInactive=true',
        );
        if (cancelled) return;
        if (r.success && Array.isArray(r.data)) setRows(r.data);
        else setLoadError(r.error ?? 'Failed to load attestation boxes.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.boxKey.trim() || !draft.boxLabel.trim()) {
      setSaveError('Box key and label are both required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const r = await client.http.post<{ success: boolean; error?: string }>(
        '/v3/admin/wet-sign-attestation-box',
        {
          headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
          body: { ...draft, helpText: draft.helpText?.trim() || null },
        },
      );
      if (!r.success) throw new Error(r.error ?? 'save failed');
      setDraft(null);
      setToast({ tone: 'info', text: `Saved ${draft.docTypeKey}/${draft.stateCode}/${draft.boxKey}.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { docTypeKey, stateCode, boxKey } = deleteTarget;
      const r = await client.http.del<{ success: boolean; error?: string }>(
        `/v3/admin/wet-sign-attestation-box/${encodeURIComponent(docTypeKey)}/${encodeURIComponent(stateCode)}/${encodeURIComponent(boxKey)}`,
        { headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL) },
      );
      if (!r.success) throw new Error(r.error ?? 'delete failed');
      setToast({ tone: 'warn', text: `Deactivated ${docTypeKey}/${stateCode}/${boxKey} (still listed as inactive).` });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = [
    { header: 'DocType', cell: (r) => <code className="cell-mono">{r.docTypeKey}</code> },
    { header: 'State', cell: (r) => <code className="cell-mono">{r.stateCode}</code> },
    { header: 'Box', cell: (r) => <code className="cell-mono--bold">{r.boxKey}</code> },
    { header: 'Label', cell: (r) => <span className="cell-strong">{r.boxLabel}</span> },
    { header: 'Order', cell: (r) => <span className="num">{r.displayOrder}</span> },
    {
      header: 'Required',
      cell: (r) => (r.isRequired ? <Pill tone="warn">Required</Pill> : <Pill tone="muted">Optional</Pill>),
    },
    { header: 'Active', cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>) },
    {
      header: '',
      cell: (r) => (
        <div className="card-actions-row">
          <Button onClick={() => { setDraft({ ...r, helpText: r.helpText ?? '', isNew: false }); setSaveError(null); }}>
            Edit
          </Button>
          <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>
        </div>
      ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load attestation boxes: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading attestation boxes…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Wet-Sign Attestation Boxes — ${rows.length}`}
        actions={
          <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
            + New box
          </Button>
        }
      >
        <p className="cell-desc">
          Per-(docType × state × box) attestation checkboxes for the FSR wet-sign modal. Wildcards:
          docTypeKey='*' / stateCode='*' apply to all. Required boxes gate the Apply button.
        </p>
        <DataTable<Row>
          columns={columns}
          rows={rows}
          rowKey={(r) => `${r.docTypeKey}::${r.stateCode}::${r.boxKey}`}
          emptyState={<p className="empty">No attestation boxes defined yet.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'New attestation box' : `Edit ${draft?.docTypeKey}/${draft?.stateCode}/${draft?.boxKey}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="DocType key ('*' = all)"
                value={draft.docTypeKey}
                onChange={(v) => setDraft({ ...draft, docTypeKey: v })}
                disabled={!draft.isNew}
                monospace
                placeholder="*"
              />
              <TextField
                label="State code ('*' = all; e.g. MO)"
                value={draft.stateCode}
                onChange={(v) => setDraft({ ...draft, stateCode: v })}
                disabled={!draft.isNew}
                monospace
                placeholder="*"
              />
            </div>

            <div className="form-row">
              <TextField
                label="Box key"
                value={draft.boxKey}
                onChange={(v) => setDraft({ ...draft, boxKey: v })}
                disabled={!draft.isNew}
                monospace
                placeholder="PhotoIdVerified"
              />
              <NumberField
                label="Display order"
                value={draft.displayOrder}
                onChange={(v) => setDraft({ ...draft, displayOrder: v ?? 100 })}
                min={1}
              />
            </div>

            <TextField
              label="Box label (FSR-facing)"
              value={draft.boxLabel}
              onChange={(v) => setDraft({ ...draft, boxLabel: v })}
              placeholder="Photo ID verified by FSR"
            />

            <TextArea
              label="Help text (hover tooltip)"
              value={draft.helpText ?? ''}
              onChange={(v) => setDraft({ ...draft, helpText: v })}
              rows={2}
            />

            <BoolField
              label="Required"
              value={draft.isRequired}
              onChange={(v) => setDraft({ ...draft, isRequired: v })}
              hint="Must be checked before the FSR can apply the attestation."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive boxes don't render in the modal."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Deactivate this attestation box?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Deactivate'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll deactivate the box{' '}
              <code className="cell-mono">{deleteTarget.docTypeKey}/{deleteTarget.stateCode}/{deleteTarget.boxKey}</code>.
              The FSR wet-sign modal stops showing it, but the row stays here (as
              inactive) for audit and can be reactivated by editing it. (This is a
              soft-delete — the backend sets IsActive=false, not a hard delete.)
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
